/**
 * D-EZ-95 — THE LEAGUE'S CONTEXT FOR THE BOOTH, AS DATA.
 *
 * Builds `BoothContext.season` for the race about to be run. Pure: archived
 * seasons plus the live season state in, a plain object out. Nothing here
 * writes a line of commentary; a later phase decides what the booth says with
 * it. Every field is something the ledgers already know.
 */

import type { BoothRivalryHeat, BoothRivalryMoment, BoothSeasonContext, BoothStanding } from '../booth/context.ts';
import { hottestRivalries, rivalryState, type RivalryMoment } from '../people/rivalry.ts';
import type { SeasonState } from '../seasonPlay.ts';
import { seedPlayoff } from '../seasonPlay.ts';
import { PLAYOFF_ROUNDS, REGULAR_SEASON_RACES } from '../schedule.ts';
import type { ScheduledRace } from '../schedule.ts';
import { MAX_RACE_POINTS, type DriverSeasonStats, type SeasonStandings } from '../standings.ts';
import type { CareerPrior } from './types.ts';
import { allRaces, liveHistorySeason, sortedSeasons, type HistorySeason } from './source.ts';
import { raceHistory, trackHistory } from './tracks.ts';
import { driverStreaks, milestonesInPlay } from './milestones.ts';
import { rivalries } from './rivalries.ts';

/** How many of the hottest pairs the booth is handed. */
const RIVALRY_HEAT_PAIRS = 12;

/**
 * D-EZ-108 — the rivalry book's hottest live pairs going into the race, as data.
 * Narrative only: read off incidents the dice already produced.
 */
function rivalryHeatFor(seasons: readonly HistorySeason[], venue: string, standings: SeasonStandings | null): BoothRivalryHeat[] {
  const book = rivalryState({ archive: seasons });
  const lite = (m: RivalryMoment | null | undefined): BoothRivalryMoment | null =>
    m
      ? {
          seasonNumber: m.seasonNumber,
          raceNumber: m.raceNumber,
          raceName: m.raceName,
          trackName: m.trackName,
          venue: m.venue,
          turn: m.turn,
          aggressorId: m.aggressorId,
          victimId: m.victimId,
          text: m.text,
        }
      : null;
  return hottestRivalries(book, RIVALRY_HEAT_PAIRS).map((r) => {
    const heated = r.moments.filter((m) => m.kind !== 'hatchet');
    const here = [...heated].reverse().find((m) => m.venue === venue) ?? null;
    const pa = standings?.byDriver[r.a]?.points;
    const pb = standings?.byDriver[r.b]?.points;
    return {
      a: r.a,
      b: r.b,
      aName: r.aName,
      bName: r.bName,
      heat: r.heat,
      state: r.state as BoothRivalryHeat['state'],
      trend: r.trend,
      headToHead: r.headToHead,
      firstSpark: lite(r.firstSpark),
      worst: lite(r.worst),
      lastIncident: lite(heated[heated.length - 1]),
      atThisTrack: lite(here),
      pointsGap: pa !== undefined && pb !== undefined ? pa - pb : null,
      teammates: r.teammates,
    };
  });
}

function standing(d: DriverSeasonStats): BoothStanding {
  return { driverId: d.driverId, name: d.name, rank: d.rank, points: d.points, wins: d.wins };
}

export interface BoothSeasonInput {
  /** Settled seasons of this league, any order. */
  archive: readonly HistorySeason[];
  /** The season being raced. */
  live: {
    seasonNumber: number;
    roster: HistorySeason['roster'];
    teams: HistorySeason['teams'];
    state: SeasonState;
  };
  /** Pre-league careers (D-EZ-97), when the league has any. */
  priors?: Readonly<Record<string, CareerPrior>>;
  /** Drivers to look for milestones among. Defaults to the live roster. */
  driverIds?: readonly string[];
}

export function buildBoothSeasonContext(input: BoothSeasonInput): BoothSeasonContext | null {
  const { state } = input.live;
  const next: ScheduledRace | undefined = state.schedule.races[state.nextRace - 1];
  if (!next) return null;
  const liveSeason = liveHistorySeason(input.live);
  const archive = sortedSeasons(input.archive.filter((s) => s.seasonNumber !== input.live.seasonNumber));
  const seasons = [...archive, liveSeason];
  const races = allRaces(seasons);
  const priors =
    input.priors ?? state.inherited?.priors ?? [...archive].reverse().find((s) => s.offseason?.carry)?.offseason?.carry?.priors ?? {};

  const standings: SeasonStandings | null = state.standingsAfterRace[state.standingsAfterRace.length - 1] ?? null;
  const ranked = standings?.drivers ?? [];

  // ---- the points race --------------------------------------------------
  const leader = ranked[0] ?? null;
  const pointsLeader = leader
    ? { ...standing(leader), marginToSecond: leader.points - (ranked[1]?.points ?? 0) }
    : null;

  // ---- the playoff picture ----------------------------------------------
  let playoff: BoothSeasonContext['playoff'] = null;
  if (standings && ranked.length > 0) {
    if (next.raceNumber <= REGULAR_SEASON_RACES) {
      const seeds = seedPlayoff(standings, () => true);
      const inOnWins = seeds.filter((s) => s.viaWin).map((s) => s.driverId);
      const lastIn = [...seeds].reverse().find((s) => !s.viaWin) ?? null;
      const cutStat = lastIn ? standings.byDriver[lastIn.driverId] : null;
      const seeded = new Set(seeds.map((s) => s.driverId));
      const bubble = cutStat
        ? ranked
            .filter((d) => !inOnWins.includes(d.driverId) && Math.abs(d.points - cutStat.points) <= MAX_RACE_POINTS && d.driverId !== cutStat.driverId)
            .map((d) => ({ ...standing(d), side: (seeded.has(d.driverId) ? 'in' : 'out') as 'in' | 'out', gap: d.points - cutStat.points }))
            .sort((a, b) => Math.abs(a.gap) - Math.abs(b.gap))
            .slice(0, 8)
        : [];
      playoff = {
        phase: 'regular-season',
        racesToCut: REGULAR_SEASON_RACES - next.raceNumber + 1,
        berths: 16,
        inOnWins,
        cutLine: cutStat ? standing(cutStat) : null,
        bubble,
        alive: [],
      };
    } else {
      const spec = PLAYOFF_ROUNDS.find((r) => next.raceNumber >= r.firstRace && next.raceNumber <= r.lastRace);
      const alive = state.playoff.contenders ? [...state.playoff.contenders] : [];
      if (spec && alive.length > 0) {
        const roundRaces: number[] = [];
        for (let n = spec.firstRace; n < next.raceNumber; n++) roundRaces.push(n);
        const roundPts = (id: string): number =>
          roundRaces.reduce((t, n) => t + (standings.byDriver[id]?.pointsByRace[n] ?? 0), 0);
        const order = [...alive].sort((a, b) => roundPts(b) - roundPts(a) || (standings.byDriver[b]?.points ?? 0) - (standings.byDriver[a]?.points ?? 0));
        const cutAt = spec.advancing ?? 1;
        const cutId = order[cutAt - 1] ?? null;
        const cutStat = cutId ? standings.byDriver[cutId] : null;
        const asRound = (id: string, i: number): BoothStanding => ({
          driverId: id,
          name: standings.byDriver[id]?.name ?? id,
          rank: i + 1,
          points: roundPts(id),
          wins: standings.byDriver[id]?.wins ?? 0,
        });
        playoff = {
          phase: 'playoff',
          racesToCut: spec.lastRace - next.raceNumber + 1,
          berths: cutAt,
          inOnWins: [],
          cutLine: cutId ? asRound(cutId, cutAt - 1) : null,
          bubble: cutStat
            ? order
                .map((id, i) => ({ ...asRound(id, i), side: (i < cutAt ? 'in' : 'out') as 'in' | 'out', gap: roundPts(id) - roundPts(cutId as string) }))
                .filter((b) => b.driverId !== cutId && Math.abs(b.gap) <= MAX_RACE_POINTS)
                .sort((a, b) => Math.abs(a.gap) - Math.abs(b.gap))
            : [],
          alive,
        };
      }
    }
  }

  // ---- who has won here -------------------------------------------------
  const named = raceHistory(races, { raceKey: next.raceKey, raceName: next.raceName });
  const atTrack = trackHistory(races, next.venue ?? next.trackId);
  const lastChamp = [...archive].reverse().find((s) => s.complete !== false && s.season.championId);
  const champId = lastChamp?.season.championId ?? null;

  // ---- milestones, streaks, rivalries ----------------------------------
  const ids = input.driverIds ?? input.live.roster.map((c) => c.driverId);
  const nameOf = (id: string): string =>
    input.live.roster.find((c) => c.driverId === id)?.name ?? standings?.byDriver[id]?.name ?? id;
  const streaks = driverStreaks(races, priors);
  const activeStreaks: BoothSeasonContext['activeStreaks'] = [];
  for (const id of ids) {
    const s = streaks[id];
    if (!s) continue;
    if (s.winStreak >= 2) activeStreaks.push({ driverId: id, name: s.name, kind: 'wins', length: s.winStreak });
    if (s.top5Streak >= 5) activeStreaks.push({ driverId: id, name: s.name, kind: 'top5', length: s.top5Streak });
    else if (s.top10Streak >= 8) activeStreaks.push({ driverId: id, name: s.name, kind: 'top10', length: s.top10Streak });
    if (s.finishStreak >= 20) activeStreaks.push({ driverId: id, name: s.name, kind: 'finishes', length: s.finishStreak });
  }
  activeStreaks.sort((a, b) => b.length - a.length);

  return {
    seasonNumber: input.live.seasonNumber,
    raceNumber: next.raceNumber,
    round: next.round,
    pointsLeader,
    playoff,
    defendingRaceWinner: named.defendingWinner
      ? { driverId: named.defendingWinner.driverId, name: named.defendingWinner.name, seasonNumber: named.defendingWinner.seasonNumber }
      : null,
    trackWinners: atTrack.byDriver
      .filter((d) => d.wins > 0 && ids.includes(d.driverId))
      .map((d) => ({ driverId: d.driverId, name: d.name, wins: d.wins })),
    defendingChampion:
      champId && lastChamp
        ? { driverId: champId, name: lastChamp.season.finalStandings.byDriver[champId]?.name ?? nameOf(champId), seasonNumber: lastChamp.seasonNumber }
        : null,
    milestonesInPlay: milestonesInPlay(races, ids, priors, nameOf).map((m) => ({
      kind: m.kind,
      driverId: m.driverId,
      name: m.name,
      n: m.n,
      text: m.text,
    })),
    activeStreaks,
    rivalryHeat: rivalryHeatFor(seasons, next.venue ?? next.trackId, standings),
    rivalries: rivalries(seasons).map((r) => ({
      a: r.a,
      b: r.b,
      aName: r.aName,
      bName: r.bName,
      feuds: r.feuds,
      races: r.races,
      seasons: r.seasons,
      live: r.live,
    })),
  };
}
